/**
 * Browser microphone recording and speech-to-text for Voice Mode and
 * dictation. `createCaptureController` returns a capture controller: a reusable
 * service that records one utterance, uploads it, and reports the transcript.
 * Voice Mode drives it directly; React dictation controls use
 * `useSpeechToText` as an adapter.
 */
import {
  MAX_SPEECH_TO_TEXT_AUDIO_BYTES,
  RecordingStatus,
  type VoiceCaptureCallbacks,
  type VoiceCaptureHandle,
} from '@littlebird/sdk';
import { toast } from 'sonner';

import { logError } from '@/lib/errorLogging';
import { littlebirdSdk } from '@/lib/sdk-init';
import { settingsStore } from '@/stores/settings.store';
import { createAudioContext } from '@/utils/audio-context';
import { makeDevEventLog } from '@/utils/dev-event-log';

import { claimSpeechActivity } from './speech-activity-handoff';
import { trackPendingTranscription } from './transcription-activity';
import { createVadFrameLog } from './vad-frame-log';
import {
  getBandEnergyRatios,
  getVoiceScore,
  getWaveformRms,
  VoiceActivityDetector,
} from './voice-activity-detector';
import { DEFAULT_VOICE_ACTIVITY_OPTIONS } from './voice-activity-options';

/** First supported entry wins; WebKit records MPEG-4 audio, not WebM */
const RECORDING_FORMATS = [
  {
    mimeType: 'audio/webm;codecs=opus',
    fileName: 'recording.webm',
    fileType: 'audio/webm',
  },
  { mimeType: 'audio/mp4', fileName: 'recording.mp4', fileType: 'audio/mp4' },
] as const;

type RecordingFormat = (typeof RECORDING_FORMATS)[number];

/** The first recording format this browser can produce, or null when none is */
function selectRecordingFormat(): RecordingFormat | null {
  if (typeof MediaRecorder === 'undefined') return null;
  return (
    RECORDING_FORMATS.find((format) => {
      try {
        return MediaRecorder.isTypeSupported(format.mimeType);
      } catch {
        return false;
      }
    }) ?? null
  );
}

let loggedUnsupportedRecorder = false;

/**
 * Why the microphone button must hide on this browser, or null when dictation
 * can run. Logs the reason once so a hidden mic is explainable in the dev log.
 */
export function getDictationUnsupportedReason(): string | null {
  let reason: string | null;
  if (typeof MediaRecorder === 'undefined') {
    reason = 'MediaRecorder is unavailable';
  } else if (selectRecordingFormat() === null) {
    reason = `none of ${RECORDING_FORMATS.map((format) => format.mimeType).join(
      ', '
    )} is supported`;
  } else {
    reason = null;
  }
  if (reason && !loggedUnsupportedRecorder) {
    loggedUnsupportedRecorder = true;
    logRecordingEvent(`dictation hidden: ${reason}`);
  }
  return reason;
}

// Chromium's default Opus bitrate is tuned for music; speech stays clearly
// transcribable at 32 kbps and uploads shrink another ~4x
const RECORDING_AUDIO_BITS_PER_SECOND = 32_000;

/** Receive compressed chunks steadily instead of one buffer at recording stop */
const RECORDING_DATA_INTERVAL_MS = 1000;

/**
 * At or below this a WebM holds little more than container metadata.
 * Borderline recordings still upload; an empty transcript then reports no
 * speech.
 */
const MIN_AUDIBLE_RECORDING_BYTES = 512;

/** Number of waveform samples in each browser audio-analysis snapshot */
const VAD_ANALYSER_FFT_SIZE = 512;

/**
 * Blend each frequency snapshot with the previous one so brief jitter is
 * less likely to look like speech
 */
const VAD_ANALYSER_SMOOTHING = 0.15;

// Product cap for one dictation take. Hitting it transcribes what was said
// rather than discarding it; ~5 MB at the 32 kbps voice bitrate.
const DICTATION_TIME_LIMIT_MS = 20 * 60 * 1000;

/**
 * Resources for one recording attempt. If the user cancels or starts again
 * while microphone or recorder setup is pending, the `activeAttempt !==
 * attempt` comparisons in `startRecording` make the obsolete attempt release
 * anything it later opens.
 */
type CaptureAttempt = {
  recorder: MediaRecorder | null;
  stream: MediaStream | null;
  /** The recording format this attempt was started with */
  format: RecordingFormat;
  completion: CaptureCompletion;
  /**
   * Recording ended normally (user stop, dictation time limit, or detected
   * end of speech) and its recorder is flushing the final audio
   */
  stopRequested: boolean;
  /**
   * Tears down this attempt's auto-stop machinery (dictation's time-limit
   * timer or the silence-detection loop). Called whenever the recording
   * ends, so nothing stale fires afterwards; null means none is armed.
   */
  autoStopCleanup: (() => void) | null;
};

export type CaptureCompletionOutcome = 'completed' | 'failed' | 'canceled';

type CaptureCompletion = {
  promise: Promise<CaptureCompletionOutcome>;
  resolve: (outcome: CaptureCompletionOutcome) => void;
};

type PendingTranscription = {
  controller: AbortController;
  completion: CaptureCompletion;
  /** Ends this upload's share of the renderer's transcription activity */
  releaseActivity: () => void;
};

/** Dictation stops manually; Voice Mode stops automatically after silence */
type RecordingStopMode = 'manual' | 'after-silence';

function createCaptureCompletion(): CaptureCompletion {
  let resolve!: (outcome: CaptureCompletionOutcome) => void;
  const promise = new Promise<CaptureCompletionOutcome>((resolvePromise) => {
    resolve = resolvePromise;
  });
  return { promise, resolve };
}

/** Stop every track in a microphone stream */
function stopMediaStreamTracks(mediaStream: MediaStream) {
  mediaStream.getTracks().forEach((track) => track.stop());
}

/** Whether microphone startup failed because the user or system denied access */
function isMicrophoneAccessDenied(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    (error.name === 'NotAllowedError' || error.name === 'SecurityError')
  );
}

/** Whether Chromium could not satisfy an exact microphone selection */
function isSelectedMicrophoneUnavailable(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    (error.name === 'NotFoundError' || error.name === 'OverconstrainedError')
  );
}

const logRecordingEvent = makeDevEventLog(
  '[voice-recording]',
  '__littlebirdRecordingLog',
  200
);

/** Stop automatic stopping and release the microphone tracks owned by one attempt */
function releaseCaptureResources(attempt: CaptureAttempt) {
  attempt.autoStopCleanup?.();
  attempt.autoStopCleanup = null;
  if (attempt.stream) stopMediaStreamTracks(attempt.stream);
  attempt.stream = null;
}

/** Stop and detach one attempt so it cannot report a late result */
function cancelCaptureAttempt(attempt: CaptureAttempt) {
  const recorder = attempt.recorder;
  if (recorder) {
    recorder.onerror = null;
    recorder.onstart = null;
    recorder.ondataavailable = null;
    recorder.onstop = null;
    if (recorder.state !== 'inactive') {
      try {
        recorder.stop();
      } catch (error) {
        logError(error, 'Failed to stop recorder during capture cancel');
      }
    }
  }
  releaseCaptureResources(attempt);
}

/**
 * Public controls for one reusable microphone recorder and transcription
 * uploader. The implementation keeps its current recording and upload private;
 * callers interact with them only through these operations and read-only refs.
 */
export interface CaptureController extends VoiceCaptureHandle {
  /** Replace the callbacks used by future recording and transcription events */
  setCallbacks(callbacks: VoiceCaptureCallbacks): void;
  /** Record until the caller stops or cancels */
  startRecording(): Promise<void>;
  /** Finish normally and report after any recorded sound has been transcribed */
  stopRecording(): Promise<CaptureCompletionOutcome>;
  /** Live microphone stream used by the on-screen level display */
  mediaStreamRef: { readonly current: MediaStream | null };
  /**
   * Cancel an unfinished recording, preserving one already stopped and
   * flushing its audio; used during unmount and speech-feature handoff
   */
  cancelRecordingUnlessFinishing(): void;
}

/**
 * Create a reusable, React-independent recorder and transcription uploader.
 * Recording and upload have separate lifetimes: cancelling a recording lets a
 * submitted upload finish, but a new recording is refused while one is pending
 * so the pending transcript is never discarded.
 *
 * When `claimSpeechActivityForRecording` is true, a recording claims the
 * shared speech-activity slot unless it already holds it, and keeps it until
 * capture ends. A new claim stops the previous holder. Voice Mode passes
 * false: its session claims the slot once at start (`onSessionStart` in the
 * desktop platform) and holds it through playback between recordings — a
 * per-recording claim would displace the session's own claim and end it.
 */
export function createCaptureController(
  initialCallbacks: VoiceCaptureCallbacks,
  claimSpeechActivityForRecording = true
): CaptureController {
  let callbacks = initialCallbacks;
  /** Current recording attempt; a replaced attempt's late work is ignored */
  let activeAttempt: CaptureAttempt | null = null;
  /**
   * The current upload, doubling as its identity token:
   * even a result that outruns the abort is ignored once this is replaced
   */
  let activeTranscription: PendingTranscription | null = null;
  let releaseSpeechActivityClaim: (() => void) | null = null;

  const controller: CaptureController = {
    setCallbacks(nextCallbacks) {
      callbacks = nextCallbacks;
    },

    cancelPendingTranscription() {
      discardTranscription();
      onCaptureStateChanged();
    },

    hasPendingTranscription() {
      return activeTranscription !== null;
    },

    isCapturing() {
      return activeAttempt !== null;
    },

    stopRecording() {
      if (!activeAttempt) {
        return (
          activeTranscription?.completion.promise ?? Promise.resolve('canceled')
        );
      }
      const attempt = activeAttempt;
      if (!activeAttempt.recorder) {
        // Microphone setup is still pending, so no audio can be finished.
        // Cancel now and make any late setup result obsolete
        controller.cancelRecording();
        return attempt.completion.promise;
      }
      activeAttempt.autoStopCleanup?.();
      activeAttempt.autoStopCleanup = null;

      // This function only requests the stop; the finishing work (audio flush,
      // upload) happens when the recorder's `onstop` event runs
      // `handleRecordingStopped`. Inactive means an earlier Stop already made
      // the request, so we bail because re-requesting would throw.
      if (activeAttempt.recorder.state === 'inactive') {
        return attempt.completion.promise;
      }
      activeAttempt.stopRequested = true;
      activeAttempt.recorder.stop();
      // Nothing else reports until `onstop` runs, so without this the flush
      // would still look like an active recording
      onCaptureStateChanged();
      return attempt.completion.promise;
    },

    cancelRecording() {
      cancelActiveRecording('canceled');
    },

    cancelRecordingUnlessFinishing() {
      if (activeAttempt?.stopRequested) return;
      controller.cancelRecording();
    },

    startRecording() {
      return startRecording('manual');
    },

    startRecordingWithAutomaticStop() {
      return startRecording('after-silence');
    },

    disposeAndCancelTranscription() {
      controller.cancelPendingTranscription();
      controller.cancelRecording();
    },

    mediaStreamRef: {
      get current() {
        return activeAttempt?.stream ?? null;
      },
    },
  };

  /**
   * Publish the controller's derived recording state. Dictation releases its
   * speech claim when capture ends, even while transcription continues.
   */
  function onCaptureStateChanged() {
    // Processing spans both the flush of a stopped recording's audio and the
    // upload that follows it: starts are refused throughout
    const status =
      activeAttempt?.recorder?.state === 'recording'
        ? RecordingStatus.Recording
        : activeTranscription || activeAttempt?.stopRequested
          ? RecordingStatus.Processing
          : RecordingStatus.None;
    if (activeAttempt === null) {
      releaseSpeechActivityClaim?.();
      releaseSpeechActivityClaim = null;
    }
    callbacks.onStatusChange(status);
  }

  /** Cancel the current attempt and settle callers waiting for its outcome */
  function cancelActiveRecording(outcome: CaptureCompletionOutcome) {
    const attempt = activeAttempt;
    if (!attempt) return;
    activeAttempt = null;
    onCaptureStateChanged();
    cancelCaptureAttempt(attempt);
    attempt.completion.resolve(outcome);
  }

  /** Mark the current upload obsolete before aborting so a racing result is ignored */
  function discardTranscription() {
    const transcription = activeTranscription;
    activeTranscription = null;
    transcription?.releaseActivity();
    transcription?.controller.abort();
    transcription?.completion.resolve('canceled');
  }

  /**
   * Start one capture attempt. Microphone permission cannot be cancelled, so
   * after each await the code re-checks that `activeAttempt` is still this
   * attempt and releases anything an obsolete attempt opened.
   */
  async function startRecording(stopMode: RecordingStopMode) {
    // A new recording's transcription would discard the pending upload's
    // transcript, so refuse to start until it resolves. UI entry points
    // already present Processing as not startable
    if (activeTranscription) return;
    // A just-stopped take is equally precious: its upload begins only when
    // the recorder's stop event fires, so a start in that gap would destroy it
    if (activeAttempt?.stopRequested) return;

    const format = selectRecordingFormat();
    if (format === null) {
      const error = new Error('No supported audio recording MIME type');
      logError(error, 'Failed to start recording');
      toast.error('Voice input is not available here', {
        description:
          'This browser cannot record audio in a format dictation can upload.',
      });
      callbacks.onFailure?.(error);
      return;
    }

    // Claim the speech-activity slot before async setup; a feature claiming
    // later stops this recording through the slot's stop callback
    if (claimSpeechActivityForRecording) {
      releaseSpeechActivityClaim ??= claimSpeechActivity(
        controller.cancelRecordingUnlessFinishing
      );
    }

    // Replace our own attempt; the speech-activity claim (kept by ??= above)
    // survives while the microphone is re-acquired
    if (activeAttempt) {
      cancelCaptureAttempt(activeAttempt);
      activeAttempt.completion.resolve('canceled');
    }
    const attempt: CaptureAttempt = {
      recorder: null,
      stream: null,
      format,
      completion: createCaptureCompletion(),
      stopRequested: false,
      autoStopCleanup: null,
    };
    activeAttempt = attempt;

    const preferredInputDeviceId = settingsStore.preferredSpeechInputDeviceId;
    try {
      const acquiredStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: { ideal: 1 },
          ...(preferredInputDeviceId
            ? { deviceId: { exact: preferredInputDeviceId } }
            : {}),
        },
      });
      // Cancellation cannot abort `getUserMedia`, so close a stream returned to
      // an obsolete attempt
      if (activeAttempt !== attempt) {
        stopMediaStreamTracks(acquiredStream);
        return;
      }
      attempt.stream = acquiredStream;

      const recorder = new MediaRecorder(acquiredStream, {
        mimeType: format.mimeType,
        audioBitsPerSecond: RECORDING_AUDIO_BITS_PER_SECOND,
      });
      attempt.recorder = recorder;
      const chunks: Blob[] = [];
      recorder.onerror = (event) => {
        if (activeAttempt !== attempt) return;
        cancelActiveRecording('failed');
        logError(event.error, 'Failed to record audio');
        toast.error('Failed to record audio');
        callbacks.onFailure?.(event.error);
      };
      recorder.onstart = onCaptureStateChanged;
      recorder.ondataavailable = (event) => chunks.push(event.data);
      recorder.onstop = () => handleRecordingStopped(attempt, chunks, stopMode);

      if (stopMode === 'after-silence') {
        await startRecordingWithVoiceActivityDetection(
          attempt,
          acquiredStream,
          recorder
        );
      } else {
        recorder.start(RECORDING_DATA_INTERVAL_MS);
        startDictationTimeLimit(attempt);
      }
    } catch (error) {
      handleRecordingStartFailure(attempt, error, preferredInputDeviceId);
    }
  }

  /**
   * Arm a timer that stops a manual dictation recording at the dictation
   * length cap. Voice Mode does not use this timer; its turns are bounded by
   * `maxRecordingMs` in the silence-detector options. Stopping transcribes
   * what was captured instead of discarding it.
   */
  function startDictationTimeLimit(attempt: CaptureAttempt) {
    const timer = setTimeout(() => {
      if (activeAttempt !== attempt) return;
      toast.info(
        'Reached the maximum recording length — transcribing what you said'
      );
      controller.stopRecording();
    }, DICTATION_TIME_LIMIT_MS);
    attempt.autoStopCleanup = () => clearTimeout(timer);
  }

  /** Finish and validate one recording before starting its transcription */
  function handleRecordingStopped(
    attempt: CaptureAttempt,
    chunks: Blob[],
    stopMode: RecordingStopMode
  ) {
    // Ignore an event queued before another start replaced this attempt
    if (activeAttempt !== attempt) return;

    activeAttempt = null;
    releaseCaptureResources(attempt);

    const recordedBytes = chunks.reduce(
      (total, chunk) => total + chunk.size,
      0
    );
    if (recordedBytes <= MIN_AUDIBLE_RECORDING_BYTES) {
      onCaptureStateChanged();
      notifyNoSpeech(stopMode);
      attempt.completion.resolve('completed');
      return;
    }
    if (recordedBytes > MAX_SPEECH_TO_TEXT_AUDIO_BYTES) {
      onCaptureStateChanged();
      toast.error('Recording is too large to transcribe', {
        description: 'Please record a shorter message.',
      });
      callbacks.onFailure?.(
        new Error('Recording exceeds the speech-to-text upload limit')
      );
      attempt.completion.resolve('failed');
      return;
    }
    // A named File stops the SDK from labelling this upload recording.wav
    void beginTranscription(
      new File(chunks, attempt.format.fileName, {
        type: attempt.format.fileType,
      }),
      () => notifyNoSpeech(stopMode),
      attempt.completion
    );
  }

  /** Release resources and report a current attempt that failed during setup */
  function handleRecordingStartFailure(
    attempt: CaptureAttempt,
    error: unknown,
    preferredInputDeviceId: string | null
  ) {
    // A stale attempt may still have acquired resources before failing
    cancelCaptureAttempt(attempt);
    if (activeAttempt !== attempt) return;
    activeAttempt = null;
    onCaptureStateChanged();
    logError(error, 'Failed to start recording');
    const permissionDenied = isMicrophoneAccessDenied(error);
    const selectedMicrophoneUnavailable =
      preferredInputDeviceId !== null && isSelectedMicrophoneUnavailable(error);
    toast.error(
      permissionDenied
        ? 'Microphone access denied'
        : selectedMicrophoneUnavailable
          ? 'Selected microphone unavailable'
          : 'Failed to record audio',
      {
        description: permissionDenied
          ? 'Allow microphone access in your browser or system settings to use voice input.'
          : selectedMicrophoneUnavailable
            ? 'Right-click the microphone button to choose another input device.'
            : 'Please check microphone access and try again.',
      }
    );
    callbacks.onFailure?.(error);
    attempt.completion.resolve('failed');
  }

  /** Report that a recording contained no speech, with dictation-only feedback */
  function notifyNoSpeech(stopMode: RecordingStopMode) {
    if (stopMode === 'manual') {
      toast.info('No speech detected. Please try again.');
    }
    notifyEmptyTranscript();
  }

  function notifyEmptyTranscript() {
    logRecordingEvent('empty-transcript');
    callbacks.onEmptyTranscript?.();
  }

  /**
   * Upload one completed recording. Only the still-current upload may deliver
   * callbacks or clear Processing state after its request settles.
   */
  async function beginTranscription(
    blob: Blob,
    notifyNoSpeech: () => void,
    completion: CaptureCompletion
  ) {
    const transcription: PendingTranscription = {
      controller: new AbortController(),
      completion,
      releaseActivity: trackPendingTranscription(),
    };
    activeTranscription = transcription;
    onCaptureStateChanged();
    callbacks.onTranscriptionStart?.();

    let completionOutcome: CaptureCompletionOutcome = 'completed';
    try {
      const transcript = await littlebirdSdk.transcribeSpeech(
        blob,
        transcription.controller.signal
      );
      // Cancel, a newer upload, or leaving Voice Mode can make this result
      // obsolete while the response is being delivered
      if (activeTranscription !== transcription) return;
      const trimmedTranscript = transcript.trim();
      if (!trimmedTranscript) {
        notifyNoSpeech();
        return;
      }
      callbacks.onTranscript(trimmedTranscript);
    } catch (error) {
      if (activeTranscription !== transcription) return;
      completionOutcome = 'failed';
      logError(error, 'Failed to convert speech to text');
      toast.error('Failed to convert speech to text', {
        description: 'Please try recording again.',
      });
      callbacks.onFailure?.(error);
    } finally {
      if (activeTranscription === transcription) {
        activeTranscription = null;
        transcription.releaseActivity();
        onCaptureStateChanged();
        completion.resolve(completionOutcome);
      }
    }
  }

  /**
   * Start one Voice Mode recording with automatic silence detection. This
   * connects the live microphone to the browser's sound analyser, starts the
   * recorder, and then begins sampling.
   *
   * About every 30 milliseconds, one analysis snapshot is reduced to its
   * volume and share of energy in speech frequencies. `VoiceActivityDetector`
   * then chooses one product action: keep listening, stop and transcribe after
   * speech, or discard the recording because no speech was found. If setup or
   * browser audio startup fails, the error propagates to `startRecording`,
   * which releases the microphone and reports the input failure.
   */
  async function startRecordingWithVoiceActivityDetection(
    attempt: CaptureAttempt,
    mediaStream: MediaStream,
    recorder: MediaRecorder
  ): Promise<void> {
    const audioContext = createAudioContext();
    if (!audioContext)
      throw new Error('Voice activity detection is unavailable');

    // The browser recorder stores the audio. This separate analyser only
    // measures volume and frequency so it can detect speech and silence
    let analyser: AnalyserNode;
    let source: MediaStreamAudioSourceNode;
    try {
      analyser = audioContext.createAnalyser();
      analyser.fftSize = VAD_ANALYSER_FFT_SIZE;
      analyser.smoothingTimeConstant = VAD_ANALYSER_SMOOTHING;
      source = audioContext.createMediaStreamSource(mediaStream);
      source.connect(analyser);
    } catch (error) {
      void audioContext.close();
      throw error;
    }

    const waveformData = new Uint8Array(analyser.fftSize);
    const frequencyData = new Uint8Array(analyser.frequencyBinCount);
    // Keep these settings fixed for this recording. Changes made in Settings
    // apply to the next recording instead of changing a timer already running
    const vadOptions = {
      ...DEFAULT_VOICE_ACTIVITY_OPTIONS,
      ...settingsStore.voiceModeDetection,
      trailingSilenceMs: settingsStore.voiceModePauseMs,
      maxRecordingMs: settingsStore.voiceModeMaxRecordingMs,
    };
    const detector = new VoiceActivityDetector(vadOptions);
    const frameLog = createVadFrameLog(vadOptions, logRecordingEvent);
    let timer: ReturnType<typeof setTimeout> | null = null;

    /** Sample the microphone and apply detector decisions until recording stops */
    const processNextMicrophoneSnapshot = () => {
      if (!attempt.autoStopCleanup) return;

      analyser.getByteTimeDomainData(waveformData);
      analyser.getByteFrequencyData(frequencyData);

      const nowMs = Date.now();
      const rms = getWaveformRms(waveformData);
      const voiceScore = getVoiceScore(
        frequencyData,
        audioContext.sampleRate,
        vadOptions.minimumVoiceFrequencyHz,
        vadOptions.maximumVoiceFrequencyHz
      );
      // One frame is this periodic snapshot's time, volume, and speech-frequency
      // score, not a separately recorded piece of audio
      const frame = { nowMs, rms, voiceScore };
      const decision = detector.addFrame(frame);
      frameLog?.onFrame({
        ...frame,
        bandRatios: getBandEnergyRatios(frequencyData, audioContext.sampleRate),
        trailingSilenceStartedAtMs: detector.getTrailingSilenceStartedAtMs(),
      });

      // The detector only chooses an outcome. This controller owns the
      // recorder, so it performs the matching stop or cancel action here
      switch (decision) {
        case 'stop_after_speech':
          // Finish the recording and send it for transcription
          frameLog?.onStopAfterSpeech(nowMs);
          controller.stopRecording();
          return;
        case 'stop_without_speech':
          // Discard the recording (no speech was confirmed in it); the
          // onEmptyTranscript callback lets Voice Mode listen again
          controller.cancelRecording();
          notifyEmptyTranscript();
          return;
        case 'continue':
          // Schedule after this snapshot finishes so analyses cannot overlap
          // Timer drift is harmless because the detector uses actual timestamps
          timer = setTimeout(processNextMicrophoneSnapshot, vadOptions.frameMs);
      }
    };

    attempt.autoStopCleanup = () => {
      if (timer) clearTimeout(timer);
      source.disconnect();
      void audioContext.close();
    };

    if (audioContext.state === 'suspended') await audioContext.resume();
    // The user may cancel while the browser is resuming audio analysis
    if (activeAttempt !== attempt) return;
    // Or the user may Stop during the resume: that nulls `autoStopCleanup` but
    // leaves the attempt active. Then cancel, don't start recording.
    if (!attempt.autoStopCleanup) {
      controller.cancelRecording();
      return;
    }
    // Start saving audio before the silence detector can stop this attempt
    recorder.start(RECORDING_DATA_INTERVAL_MS);
    processNextMicrophoneSnapshot();
  }

  return controller;
}
